"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import ReactDOM from "react-dom";
import Theme from "../../Theme/index.js";
import s from "./Portal.module.css";
import useIsomorphicLayoutEffect from "../../../hooks/useIsomorphicLayoutEffect.js";
import useToggle from "../../../hooks/useToggle.js";
const PortalScopeContext = React.createContext({});
export const usePortalScope = () => {
    return React.useContext(PortalScopeContext);
};
/**
 * Disclaimer: Works only for components that don't show the portal immediately
 * That gives Portal time to receive scope on first render
 */
const Portal = (props) => {
    const { children, targetRef } = props;
    const mountedToggle = useToggle();
    const rootRef = React.useRef(null);
    // eslint-disable-next-line react-hooks/refs
    const rootNode = rootRef.current?.getRootNode();
    const isShadowDom = rootNode instanceof ShadowRoot;
    const defaultTargetEl = isShadowDom ? rootNode : document.body;
    /**
     * Check for parent portal to render inside it
     * To avoid z-index issues
     * Example:
     * Dropdown rendered on the page should render under the modal
     * Dropdown inside the modal should render above it
     */
    const portal = usePortalScope();
    const nextScopeRef = targetRef || portal.scopeRef;
    // eslint-disable-next-line react-hooks/refs
    const targetEl = nextScopeRef?.current || defaultTargetEl;
    useIsomorphicLayoutEffect(() => {
        mountedToggle.activate();
        return () => mountedToggle.deactivate();
    }, []);
    /* Preserve the current theme when rendered in body */
    return [
        // eslint-disable-next-line react-hooks/refs
        ReactDOM.createPortal(_jsx(Theme, { children: children }), targetEl),
        // Make sure this element doesn't affect components using :last-child when their children use portals
        !mountedToggle.active && _jsx("div", { ref: rootRef, className: s.root }, "root"),
    ];
};
function PortalScope(props) {
    const { children } = props;
    const ref = React.useRef(null);
    return (_jsx(PortalScopeContext.Provider, { value: { scopeRef: ref }, children: children(ref) }));
}
Portal.Scope = PortalScope;
Portal.displayName = "Portal";
PortalScope.displayName = "PortalScope";
export default Portal;
